import {CoreDI} from '@gongxigames/framework/core/configs';
import {inject} from '@gongxigames/framework/core/installer';
import {slotStore} from '@gongxigames/framework/slotV2/globals';
import {UiServiceV2} from '@gongxigames/framework/slotV2/baseClasses/uiServiceV2/UIServiceV2';
import {SafeZone} from '@gongxigames/framework/core/fragments/display/SafeZone/SafeZone';
import {Rectangle} from 'pixi.js';
import {safeZoneStore} from './safeZoneStore';
import type {System} from '@gongxigames/framework/core/services/system/System';
import type {GameConfig} from '@gongxigames/framework/slot/types';
import type {ResizeEventData} from '@gongxigames/framework/core/types';

export class SafeZoneService extends UiServiceV2 {
    public static NAME: symbol = CoreDI.SafeZoneService;

    @inject(CoreDI.GameConfig)
    private readonly _gameConfig!: GameConfig;

    @inject(CoreDI.System)
    private readonly _system!: System;

    public onInit(): void {
        this.configureContainerHierarchy();
    }

    public onSetupEvents(): void {
        this.emitter.on('CORE.screen.resize', this.onResize.bind(this));
    }

    private onResize(resizeData: ResizeEventData) {
        const uiLayout = slotStore.ui_layout.value!;

        if (!uiLayout) {
            return;
        }

        this.calcSafeZoneUI(resizeData);
        this.calcSafeZoneGame(resizeData);
        this.updateSafeZoneContainers();
    }

    private calcSafeZoneUI(resizeData: ResizeEventData) {
        const uiLayout = slotStore.ui_layout.value!;
        const {width, height, orientation} = resizeData;

        const uiZone = new SafeZone({
            orientation,
            screenSize: {width, height},
            frame: new Rectangle(0, 0, width, height),
            contentSize: uiLayout.uiAreaSize[orientation],
        });

        safeZoneStore.stage_safeZoneState_ui.value = uiZone;
    }

    private calcSafeZoneGame(resizeData: ResizeEventData) {
        const {width, height, orientation} = resizeData;
        const {gameAreaSize} = this._gameConfig;
        const frame = this.getGameFrame(resizeData);

        if (!gameAreaSize) {
            return;
        }

        const gameZone = new SafeZone({
            orientation,
            screenSize: {width, height},
            frame,
            contentSize: gameAreaSize[orientation],
        });

        safeZoneStore.stage_safeZoneState_game.value = gameZone;
    }

    private getGameFrame(resizeData: ResizeEventData): Rectangle {
        const uiLayout = slotStore.ui_layout.value!;
        const {width, height, orientation} = resizeData;
        const gameWindow = uiLayout.gameWindow[orientation];
        const uiZone = safeZoneStore.stage_safeZoneState_ui.value;

        // calculate delta values to stretch the game window
        const deltaX = width - (uiZone.options.contentSize.width * uiZone.scale);
        const deltaY = height - (uiZone.options.contentSize.height * uiZone.scale);

        const frame = new Rectangle(
            gameWindow.x * uiZone.scale,
            gameWindow.y * uiZone.scale,
            gameWindow.width * uiZone.scale,
            gameWindow.height * uiZone.scale,
        );

        frame.width += deltaX;
        frame.height += deltaY;

        return frame;
    }

    private updateSafeZoneContainers() {
        const {
            stage_safeZoneState_ui,
            stage_safeZoneState_game,
            stage_safeZoneContainer_ui,
            stage_safeZoneContainer_game,
        } = safeZoneStore.values();

        stage_safeZoneContainer_ui.scale.set(stage_safeZoneState_ui.scale);
        stage_safeZoneContainer_ui.position.copyFrom(stage_safeZoneState_ui.position);

        stage_safeZoneContainer_game.scale.set(stage_safeZoneState_game.scale);
        stage_safeZoneContainer_game.position.copyFrom(stage_safeZoneState_game.position);
    }

    private configureContainerHierarchy(): void {
        const {
            stage_rootContainer,

            stage_safeZoneContainer_game,
            stage_rootContainer_game,

            stage_safeZoneContainer_ui,
            stage_rootContainer_ui,
        } = safeZoneStore.values();

        stage_rootContainer.name = 'SlotV2';
        stage_safeZoneContainer_ui.name = 'SafeZoneUI';
        stage_safeZoneContainer_game.name = 'SafeZoneGame';
        stage_rootContainer_ui.name = 'root_ui';
        stage_rootContainer_game.name = 'root_game';

        stage_rootContainer.addChild(stage_safeZoneContainer_game, stage_safeZoneContainer_ui);
        this._system.app.stage.addChild(stage_rootContainer);

        stage_safeZoneContainer_game.addChild(stage_rootContainer_game);
        stage_safeZoneContainer_ui.addChild(stage_rootContainer_ui);
    }
}